import UserSettings from '@vg/user-settings';
import { CnpTagId } from '@vgtv/api-client/lib/cnp_tag';
import { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import { ConsentManager } from '@vgtv/consent-manager';

import type { Plugin, RegisteredPlugin } from '../../types/global';
import { getRawAsset, getTagIds, isLiveStream } from '../../utils/player/asset';
import sessionStorage from '../../config/paywall/sessionStorage';

const USER_SETTINGS_TAG_KEY = 'vgtv_subscribed_tag_ids';
const USER_SETTINGS_CATEGORY_KEY = 'vgtv_subscribed_category_ids';
const PURCHASED_ASSET_SESSION_KEY = 'purchased:assetId';

const NOTIFICATION_TAG_IDS = [
  'db988981-8f46-4411-87e4-9c2b2e5a518d', // Serie A
  '7c32b85d-a8b0-43cd-9a60-836a78496dd0', // Coppa Italia
  'f0320019-a628-48b6-a01a-2366fd63686b', // Arsenal FC
  '66266b46-bd9b-4d06-a0a8-0d1e39071f5a', // Treningskamp
  'e532ab62-3227-4df2-85f8-3c71882d2a31', // Norge U21
  'bc71f41f-ecdc-477c-91ad-e79620cecacf', // Liverpool FC
  'e59616f1-8959-4a21-9035-df60657291b1', // Copa del Rey
  '4559a0d8-6abd-480a-97ce-39d46f588d8e', // ATP
  '033fe938-a6de-495b-b756-68e7cb943c35', // Davis Cup
  'f9aea1e1-5817-4526-985a-4c1b48ef7ac3', // Boksing
  'd5c06160-0139-41a5-82e9-1e42140bbdec', // MLS
  '4a886bb9-eba9-4041-b13e-aa8ed63eac8a', // Bryting
  '3f211559-d637-4fb9-aa27-aff28e5b8304', // NBA
  'b94900c9-5df2-462c-9163-dfade1b7fbd4', // Europa Conference League
] as CnpTagId[];

const NOTIFICATION_CATEGORY_IDS = [
  100465, // Av med maska
] as SvpCategoryId[];

interface TopicSubscriberPluginConfig {
  getUserSettings(): Promise<UserSettings>;
  consentManager?: ConsentManager;
}

interface TopicSubscriberPlugin
  extends Plugin<
    TopicSubscriberPlugin,
    { options: TopicSubscriberPluginConfig }
  > {
  options: TopicSubscriberPluginConfig;
  isPlayerPlaying: boolean;
  userSettings: UserSettings | null;

  manageTagSubscriptions(
    this: RegisteredPlugin<TopicSubscriberPlugin>
  ): Promise<void>;
  manageCategorySubscriptions(
    this: RegisteredPlugin<TopicSubscriberPlugin>
  ): Promise<void>;
  getUserSettings(
    this: RegisteredPlugin<TopicSubscriberPlugin>
  ): Promise<UserSettings>;
  getSubscribedTagIds(
    this: RegisteredPlugin<TopicSubscriberPlugin>
  ): Promise<CnpTagId[]>;
  setSubscribedTagIds(
    this: RegisteredPlugin<TopicSubscriberPlugin>,
    tagIds: CnpTagId[]
  ): Promise<void>;
  getSubscribedCategoryIds(
    this: RegisteredPlugin<TopicSubscriberPlugin>
  ): Promise<SvpCategoryId[]>;
  setSubscribedCategoryIds(
    this: RegisteredPlugin<TopicSubscriberPlugin>,
    tagIds: SvpCategoryId[]
  ): Promise<void>;
  getNotificationTagIds(
    this: RegisteredPlugin<TopicSubscriberPlugin>
  ): CnpTagId[];
  isPersonalizationAllowed(
    this: RegisteredPlugin<TopicSubscriberPlugin>
  ): Promise<boolean>;
}

const plugin: TopicSubscriberPlugin = {
  options: {
    getUserSettings() {
      return Promise.reject();
    },
  },
  isPlayerPlaying: false,

  userSettings: null,

  initialize(config) {
    this.options = config.options;
  },

  getName() {
    return 'TopicSubscriberPlugin';
  },

  setup() {
    this.listenTo(this.player, 'ready', async () => {
      await this.manageTagSubscriptions();
      await this.manageCategorySubscriptions();
    });

    this.listenTo(this.player, 'play adPlay', async () => {
      this.isPlayerPlaying = true;

      await this.manageTagSubscriptions();
      await this.manageCategorySubscriptions();
    });

    this.listenTo(this.player, 'pause adPause stop', () => {
      this.isPlayerPlaying = false;
    });
  },

  async getUserSettings() {
    if (!this.userSettings) {
      this.userSettings = await this.options.getUserSettings();
    }

    return this.userSettings;
  },

  async getSubscribedTagIds() {
    const userSettings = await this.getUserSettings();

    const subscribedTagIds = await userSettings.get<CnpTagId[]>(
      USER_SETTINGS_TAG_KEY
    );

    return subscribedTagIds || [];
  },

  async setSubscribedTagIds(tagIds: CnpTagId[]) {
    const userSettings = await this.getUserSettings();

    await userSettings.set(USER_SETTINGS_TAG_KEY, tagIds);
  },

  getNotificationTagIds() {
    const asset = getRawAsset(this.player);
    if (!asset) {
      return [];
    }
    const tagIds = getTagIds(asset);

    return tagIds.filter((tagId) => NOTIFICATION_TAG_IDS.includes(tagId));
  },

  async isPersonalizationAllowed() {
    // `CMP:personalisation` is hanging forever in production
    // const permission = await this.options.consentManager?.getPermissionAsync(
    //   'CMP:personalisation'
    // );
    //
    // return permission === '1';
    return true;
  },

  async manageTagSubscriptions() {
    if (!(await this.isPersonalizationAllowed())) {
      return;
    }

    const asset = getRawAsset(this.player);
    if (!asset) {
      return;
    }

    const currentAssetId = asset.id;
    const purchasedAssetId = sessionStorage.get(PURCHASED_ASSET_SESSION_KEY);

    const isPlayingLiveAsset = this.isPlayerPlaying && isLiveStream(asset);
    const hasPurchasedCurrentAsset = purchasedAssetId === currentAssetId;

    if (!isPlayingLiveAsset && !hasPurchasedCurrentAsset) {
      return;
    }

    if (hasPurchasedCurrentAsset) {
      sessionStorage.clear(PURCHASED_ASSET_SESSION_KEY);
    }

    const notificationTagIds = this.getNotificationTagIds();

    if (!notificationTagIds.length) {
      return;
    }

    const subscribedTagIds = await this.getSubscribedTagIds();

    const notSubscribedTagIds = notificationTagIds.filter(
      (tagId) => !subscribedTagIds.includes(tagId)
    );

    if (!notSubscribedTagIds.length) {
      return;
    }

    await this.setSubscribedTagIds([
      ...subscribedTagIds,
      ...notSubscribedTagIds,
    ]);
  },

  async getSubscribedCategoryIds() {
    const userSettings = await this.getUserSettings();

    const subscribedCategoryIds = await userSettings.get<SvpCategoryId[]>(
      USER_SETTINGS_CATEGORY_KEY
    );

    return subscribedCategoryIds || [];
  },

  async setSubscribedCategoryIds(categoryIds: SvpCategoryId[]) {
    const userSettings = await this.getUserSettings();

    await userSettings.set(USER_SETTINGS_CATEGORY_KEY, categoryIds);
  },

  async manageCategorySubscriptions() {
    if (!(await this.isPersonalizationAllowed())) {
      return;
    }

    const asset = getRawAsset(this.player);
    if (!asset) {
      return;
    }

    const categoryId = asset.category.id;
    if (!categoryId || !NOTIFICATION_CATEGORY_IDS.includes(categoryId)) {
      return;
    }

    const currentAssetId = asset.id;
    const purchasedAssetId = sessionStorage.get(PURCHASED_ASSET_SESSION_KEY);

    const hasPurchasedCurrentAsset = purchasedAssetId === currentAssetId;

    if (!this.isPlayerPlaying && !hasPurchasedCurrentAsset) {
      return;
    }

    if (hasPurchasedCurrentAsset) {
      sessionStorage.clear(PURCHASED_ASSET_SESSION_KEY);
    }

    const subscribedCategoryIds = await this.getSubscribedCategoryIds();

    if (subscribedCategoryIds.includes(categoryId)) {
      return;
    }

    await this.setSubscribedCategoryIds([...subscribedCategoryIds, categoryId]);
  },
};

window.SVP.Player.Plugins.register(plugin);
